// Carte des Artisans - Vue intégrée au Cockpit
// Connectée à l'API réelle /api/search/artisans/map

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MapPin, X, Star, Search,
  Building2, MessageCircle, Store,
  Hammer, Droplets, Zap, Flame, Layers, TreeDeciduous,
  HardHat, Paintbrush, Ruler, CheckCircle2, Loader2,
  Wrench, Wind, Snowflake, Key, Square, Truck, UtensilsCrossed,
  Compass, FileSearch, Briefcase, ChevronRight, ChevronLeft, ChevronDown, Navigation
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Liste des métiers pour le filtre - IDENTIQUE À MOBILE ACTU
const METIERS_DATA = [
  { id: 'tous', label: 'Tous', color: '#1E3A5F' },
  { id: 'pisciniste', label: 'Pisciniste', color: '#06B6D4' },
  { id: 'plombier', label: 'Plombier', color: '#3B82F6' },
  { id: 'electricien', label: 'Électricien', color: '#EAB308' },
  { id: 'macon', label: 'Maçon', color: '#EA580C' },
  { id: 'carreleur', label: 'Carreleur', color: '#10B981' },
  { id: 'peintre', label: 'Peintre', color: '#EC4899' },
  { id: 'menuisier', label: 'Menuisier', color: '#D97706' },
  { id: 'couvreur', label: 'Couvreur', color: '#8B5CF6' },
  { id: 'chauffagiste', label: 'Chauffagiste', color: '#EF4444' },
  { id: 'paysagiste', label: 'Paysagiste', color: '#22C55E' },
  { id: 'climaticien', label: 'Climaticien', color: '#0891B2' },
  { id: 'jardinier', label: 'Jardinier', color: '#16A34A' },
  { id: 'facadier', label: 'Façadier', color: '#A855F7' },
  { id: 'serrurier', label: 'Serrurier', color: '#64748B' },
  { id: 'charpentier', label: 'Charpentier', color: '#92400E' },
  { id: 'plaquiste', label: 'Plaquiste', color: '#6366F1' },
  { id: 'cuisiniste', label: 'Cuisiniste', color: '#F97316' },
  { id: 'architecte', label: 'Architecte', color: '#1E3A5F' },
];

// Options de rayon
const RAYON_OPTIONS = [
  { value: '10', label: '10 km' },
  { value: '25', label: '25 km' },
  { value: '50', label: '50 km' },
  { value: '100', label: '100 km' },
];

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Custom marker icon creator - Couleur uniforme orange
const createCustomIcon = () => {
  return L.divIcon({
    className: 'custom-artisan-marker',
    html: `
      <div style="
        width: 36px;
        height: 36px;
        background: #F97316;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        border: 3px solid white;
      ">
        <div style="
          transform: rotate(45deg);
          color: white;
          font-size: 14px;
        ">★</div>
      </div>
    `,
    iconSize: [36, 36],
    iconAnchor: [18, 36],
    popupAnchor: [0, -36],
  });
};

// Map controller component
function MapController({ center, zoom }) {
  const map = useMap();

  useEffect(() => {
    if (center) {
      map.setView(center, zoom || 10);
    }
  }, [center, zoom, map]);

  return null;
}

// Zoom fluide au clic sur la carte
function MapClickZoom() {
  const map = useMap();
  useMapEvents({
    click: (e) => {
      const currentZoom = map.getZoom();
      if (currentZoom < 18) {
        map.flyTo(e.latlng, currentZoom + 1, { animate: true, duration: 0.4 });
      }
    },
  });
  return null;
}

// Recalcule la taille de la carte dès que son conteneur change de dimensions
// (sidebar Abracada collapse/expand, redimensionnement fenêtre, etc.)
function MapResizeObserver() {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    if (!container || typeof ResizeObserver === 'undefined') return;

    let timeoutId;
    const observer = new ResizeObserver(() => {
      clearTimeout(timeoutId);
      // Attendre la fin de l'animation CSS de la sidebar (~300ms)
      timeoutId = setTimeout(() => {
        map.invalidateSize();
      }, 300);
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, [map]);

  return null;
}

// Artisan detail panel
function ArtisanDetailPanel({ artisan, onClose, onViewProfile }) {
  const metierData = METIERS_DATA.find(m => m.id === artisan?.metier?.toLowerCase()) || METIERS_DATA[0];
  const MetierIcon = metierData?.icon || Building2;
  
  if (!artisan) return null;
  
  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      className="absolute top-0 right-0 bottom-0 w-full max-w-sm bg-white z-[1000] flex flex-col overflow-hidden shadow-2xl rounded-l-2xl"
    >
      {/* Header */}
      <div className="relative p-4 bg-gradient-to-br from-[#1E3A5F] to-[#2F4EA2]">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
        >
          <X size={18} className="text-white" />
        </button>
        
        <div className="flex items-center gap-4">
          {artisan.photo ? (
            <img
              src={artisan.photo}
              alt={artisan.shop_name}
              className="w-16 h-16 rounded-xl object-cover border-2 border-white/30"
            />
          ) : (
            <div className="w-16 h-16 rounded-xl flex items-center justify-center bg-white/20">
              <MetierIcon size={32} className="text-white" />
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-white text-lg">{artisan.shop_name}</h3>
              {artisan.is_verified && (
                <CheckCircle2 size={18} className="text-emerald-400" fill="#10B981" />
              )}
            </div>
            <p className="text-white/80 text-sm">{artisan.metier}</p>
            <div className="flex items-center gap-1 mt-1">
              <Star size={14} className="text-amber-400 fill-amber-400" />
              <span className="text-white font-semibold">{artisan.rating?.toFixed(1) || '0.0'}</span>
              <span className="text-white/60 text-sm">({artisan.reviews_count || 0} avis)</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 p-4 space-y-4 overflow-y-auto">
        {/* Location */}
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
          <MapPin size={20} className="text-[#F97316]" />
          <div>
            <p className="font-medium text-gray-900">{artisan.city}</p>
            <p className="text-sm text-gray-500">{artisan.postal_code}</p>
          </div>
        </div>
        
        {/* Description */}
        {artisan.description && (
          <div className="p-3 bg-gray-50 rounded-xl">
            <p className="text-sm text-gray-700">{artisan.description}</p>
          </div>
        )}
        
        {/* Certifications */}
        {artisan.certifications && artisan.certifications.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {artisan.certifications.map((cert, idx) => (
              <span 
                key={idx}
                className="px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full"
              >
                {cert}
              </span>
            ))}
          </div>
        )}
      </div>
      
      {/* Footer Actions */}
      <div className="p-4 border-t border-gray-200 bg-white space-y-2">
        <Button
          onClick={() => onViewProfile(artisan)}
          className="w-full rounded-xl bg-[#F97316] hover:bg-[#EA580C]"
        >
          <Store size={16} className="mr-2" />
          Voir la fiche complète
        </Button>
        <Button
          onClick={() => onViewProfile(artisan, 'contact')}
          variant="outline"
          className="w-full rounded-xl"
        >
          <MessageCircle size={16} className="mr-2" />
          Contacter
        </Button>
      </div>
    </motion.div>
  );
}

// Main component
export default function ArtisansMapView({ embedded = false, compactMode = false, onBack, sidebarWidth, mobileMode = false }) {
  const navigate = useNavigate();
  const [artisans, setArtisans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedArtisan, setSelectedArtisan] = useState(null);
  const [selectedMetier, setSelectedMetier] = useState('tous');
  const [mapCenter, setMapCenter] = useState([46.603354, 1.888334]); // Centre France
  const [mapZoom, setMapZoom] = useState(6);
  const [userLocation, setUserLocation] = useState(null);
  
  // Search filters
  const [postalCode, setPostalCode] = useState('');
  const [selectedRayon, setSelectedRayon] = useState('50');
  
  // Scroll refs
  const filtersScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  
  // Check scroll position
  const checkScroll = useCallback(() => {
    if (filtersScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = filtersScrollRef.current;
      setCanScrollLeft(scrollLeft > 5);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
    }
  }, []);
  
  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [checkScroll]);
  
  // Scroll handlers
  const scrollFilters = (direction) => {
    if (filtersScrollRef.current) {
      const scrollAmount = 250;
      filtersScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(checkScroll, 350);
    }
  };
  
  // Request geolocation
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude } = pos.coords;
          setUserLocation([latitude, longitude]);
          setMapCenter([latitude, longitude]);
          setMapZoom(11);
          
          // Reverse geocoding to get postal code
          try {
            const response = await fetch(
              `https://api-adresse.data.gouv.fr/reverse/?lon=${longitude}&lat=${latitude}`
            );
            const data = await response.json();
            if (data.features && data.features.length > 0) {
              const pc = data.features[0].properties?.postcode;
              if (pc) {
                setPostalCode(pc);
              }
            }
          } catch (e) {
            console.log('Reverse geocoding failed:', e);
          }
        },
        () => {
          setMapCenter([46.603354, 1.888334]);
          setMapZoom(6);
        }
      );
    }
  }, []);
  
  // Fetch artisans from API
  const fetchArtisans = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (selectedMetier && selectedMetier !== 'tous') {
        params.append('metier', selectedMetier);
      }
      if (postalCode) {
        params.append('postal_code', postalCode);
      }
      if (selectedRayon) {
        params.append('rayon', selectedRayon);
      }
      params.append('limit', '50');
      
      const response = await fetch(`${API_URL}/api/search/artisans/map?${params}`);
      
      if (!response.ok) {
        throw new Error('Erreur lors du chargement des artisans');
      }
      
      const data = await response.json();
      
      setArtisans(data.artisans || []);
      
      if (data.center) {
        setMapCenter([data.center.lat, data.center.lng]);
      }
      if (data.zoom) {
        setMapZoom(data.zoom);
      }
      
    } catch (e) {
      console.error('Error fetching artisans:', e);
      setArtisans([]);
    } finally {
      setLoading(false);
    }
  }, [selectedMetier, postalCode, selectedRayon]);
  
  useEffect(() => {
    fetchArtisans();
  }, [fetchArtisans]);
  
  // Navigate to artisan profile
  const handleViewProfile = (artisan) => {
    if (artisan.slug) {
      navigate(`/boutique/${artisan.slug}`);
    } else {
      navigate(`/artisans`);
    }
  };
  
  // Search handler
  const handleSearch = () => {
    fetchArtisans();
  };
  
  const [geolocLoading, setGeolocLoading] = useState(false);
  const [geolocError, setGeolocError] = useState(null);

  // Géolocaliser l'utilisateur à la demande
  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setGeolocError('Géolocalisation non supportée par ce navigateur.');
      return;
    }
    setGeolocLoading(true);
    setGeolocError(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setUserLocation([latitude, longitude]);
        setMapCenter([latitude, longitude]);
        setMapZoom(12);
        try {
          const res = await fetch(
            `https://api-adresse.data.gouv.fr/reverse/?lon=${longitude}&lat=${latitude}`
          );
          const data = await res.json();
          if (data.features?.[0]?.properties?.postcode) {
            setPostalCode(data.features[0].properties.postcode);
          }
        } catch (_) {}
        setGeolocLoading(false);
      },
      (err) => {
        if (err.code === 1) {
          setGeolocError('Permission refusée. Cliquez sur l\'icône 🔒 dans la barre d\'adresse pour autoriser la localisation.');
        } else if (err.code === 2) {
          setGeolocError('Position introuvable. Entrez votre code postal manuellement.');
        } else {
          setGeolocError('Délai dépassé. Réessayez ou entrez votre code postal.');
        }
        setGeolocLoading(false);
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };
  
  // Detect if we're on mobile (used for conditional rendering)
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  
  return (
    <div
      className={embedded || mobileMode ? 'relative bg-gray-100' : `relative bg-gray-100 ${compactMode ? 'h-full' : 'min-h-screen'}`}
      style={mobileMode ? {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 9998,
      } : embedded ? {
        position: 'fixed', top: 0, left: sidebarWidth ?? 288, right: 0, bottom: 0,
        zIndex: 40,
        transition: 'left 0.3s ease'
      } : {}}
      data-testid="artisans-map-view"
    >
      {/* Header premium avec filtres */}
      <div className="absolute top-0 left-0 right-0 z-[500] bg-white border-b border-gray-200 shadow-sm">

        {/* ═══ MOBILE: Retour + Filtres métiers ═══ */}
        <div className="md:hidden">
          {mobileMode && (
            <div
              style={{
                paddingTop: 'max(env(safe-area-inset-top), 10px)',
                paddingLeft: '12px',
                paddingRight: '12px',
                paddingBottom: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                borderBottom: '1px solid #F3F4F6',
              }}
            >
              <button
                onClick={() => onBack ? onBack() : navigate('/artisans')}
                className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 font-semibold text-sm rounded-xl border border-gray-200 active:scale-95 transition-all flex-shrink-0"
              >
                <ChevronLeft size={16} />
                Artisans
              </button>
              <span className="flex-1 text-center text-sm font-semibold text-gray-900">Carte</span>
              <div className="w-20 flex-shrink-0" />
            </div>
          )}
          <div className="relative bg-white">
            <div
              className="flex items-center gap-2 px-3 py-2 overflow-x-auto"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {METIERS_DATA.map(m => (
                <button
                  key={m.id}
                  onClick={() => setSelectedMetier(m.id)}
                  className={`flex-shrink-0 px-4 py-2.5 rounded-2xl text-sm font-semibold whitespace-nowrap transition-all ${
                    selectedMetier === m.id
                      ? 'bg-[#F97316] text-white shadow-sm'
                      : 'bg-white text-gray-700 border border-gray-200'
                  }`}
                  data-testid={`carte-metier-${m.id}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            {/* Fade + flèche scroll */}
            <div
              className="absolute right-0 top-0 bottom-0 w-10 pointer-events-none flex items-center justify-end pr-1"
              style={{ background: 'linear-gradient(to right, transparent, white 60%)' }}
            >
              <ChevronRight size={16} className="text-gray-400" />
            </div>
          </div>
        </div>

        {/* ═══ DESKTOP: Header premium ═══ */}
        <div className="hidden md:block">
          {/* Ligne 1 : [← Retour] [Titre] ————————————————— [🔍 Rechercher] */}
          <div className="flex items-center gap-4 px-5 py-3 border-b border-gray-100">
            {/* Gauche : Retour + Titre */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {embedded && (
                <button
                  onClick={() => onBack ? onBack() : navigate('/artisans')}
                  className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 font-medium text-sm rounded-xl border border-gray-200 transition-colors flex-shrink-0"
                >
                  <ChevronLeft size={16} />
                  Retour
                </button>
              )}
              <div className="min-w-0">
                <h1 className="font-bold text-gray-900 text-xl flex items-center gap-2 leading-tight">
                  <MapPin size={22} className="text-[#F97316] flex-shrink-0" />
                  Artisans près de chez vous
                </h1>
                <p className="text-xs text-gray-400 mt-0.5 ml-8">
                  {loading ? 'Recherche en cours…' : `${artisans.length} artisan${artisans.length !== 1 ? 's' : ''} trouvé${artisans.length !== 1 ? 's' : ''}`}
                </p>
              </div>
            </div>

            {/* Droite : Bouton Rechercher */}
            <button
              onClick={handleSearch}
              className="h-10 px-6 bg-[#F97316] hover:bg-[#EA580C] text-white rounded-xl flex items-center gap-2 text-sm font-semibold transition-colors shadow-sm flex-shrink-0"
            >
              <Search size={16} />
              Rechercher
            </button>
          </div>

          {/* Ligne 2 : [📍 géoloc] [Code postal] [50 km ▼] */}
          <div className="flex items-center gap-3 px-5 py-2.5 border-b border-gray-100 bg-gray-50/40">
            <div className="flex flex-col gap-1">
              <button
                onClick={useCurrentLocation}
                disabled={geolocLoading}
                className="h-9 px-3 flex items-center gap-2 bg-[#1E3A5F] hover:bg-[#152a45] disabled:opacity-60 text-white text-sm font-medium rounded-xl transition-colors flex-shrink-0 whitespace-nowrap"
                title="Me géolocaliser"
              >
                {geolocLoading ? <Loader2 size={15} className="animate-spin" /> : <Navigation size={15} />}
                {geolocLoading ? 'Localisation…' : 'Me géolocaliser'}
              </button>
              {geolocError && (
                <p className="text-xs text-red-500 max-w-[200px]">{geolocError}</p>
              )}
            </div>
            <input
              type="text"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
              placeholder="Code postal"
              className="w-36 flex-none h-9 pl-3 pr-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F97316]/30 focus:border-[#F97316] bg-white"
              maxLength={5}
            />
            <div className="relative flex-none">
              <select
                value={selectedRayon}
                onChange={(e) => setSelectedRayon(e.target.value)}
                className="h-9 w-32 pl-3 pr-8 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#F97316]/30 focus:border-[#F97316] cursor-pointer appearance-none"
              >
                {RAYON_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            </div>
          </div>

          {/* Ligne 3 : Chips métiers — style Blog */}
          <div className="relative bg-white">
            <div
              ref={filtersScrollRef}
              className="flex items-center gap-2 px-5 py-3 overflow-x-auto"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              onScroll={checkScroll}
            >
              {METIERS_DATA.map(m => (
                <button
                  key={m.id}
                  onClick={() => setSelectedMetier(m.id)}
                  className={`flex-shrink-0 px-5 py-3 rounded-2xl text-sm font-semibold whitespace-nowrap transition-all ${
                    selectedMetier === m.id
                      ? 'bg-[#F97316] text-white shadow-sm'
                      : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`carte-metier-desktop-${m.id}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            {/* Fade gradient droit */}
            <div
              className="absolute right-0 top-0 bottom-0 w-14 pointer-events-none"
              style={{ background: 'linear-gradient(to right, transparent, white 80%)' }}
            />
          </div>
        </div>
      </div>
      
      {/* Leaflet Map - Mobile: colle aux filtres (32px), Desktop: sous le header complet */}
      <div className="absolute inset-0 pt-[54px] md:pt-[186px]">
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapController center={mapCenter} zoom={mapZoom} />
          <MapClickZoom />
          <MapResizeObserver />
          
          {/* Artisan markers */}
          {artisans.map((artisan) => {
            if (!artisan.latitude || !artisan.longitude) return null;
            
            return (
              <Marker
                key={artisan.id}
                position={[artisan.latitude, artisan.longitude]}
                icon={createCustomIcon()}
                eventHandlers={{
                  click: () => setSelectedArtisan(artisan),
                }}
              >
                <Popup>
                  <div className="min-w-[200px] p-1">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="font-bold text-gray-900">{artisan.shop_name}</div>
                      {artisan.is_verified && (
                        <CheckCircle2 size={14} className="text-blue-500" fill="#3B82F6" />
                      )}
                    </div>
                    <div className="text-sm text-gray-600">{artisan.metier} • {artisan.city}</div>
                    <div className="flex items-center gap-1 mt-1">
                      <Star size={12} className="text-amber-400 fill-amber-400" />
                      <span className="text-sm font-medium">{artisan.rating?.toFixed(1) || '0.0'}</span>
                      <span className="text-xs text-gray-500">({artisan.reviews_count || 0})</span>
                    </div>
                    <button 
                      onClick={() => setSelectedArtisan(artisan)}
                      className="mt-2 w-full py-1.5 bg-[#F97316] text-white text-xs rounded font-medium hover:bg-[#EA580C]"
                    >
                      Voir les détails
                    </button>
                  </div>
                </Popup>
              </Marker>
            );
          })}
          
          {/* User location marker */}
          {userLocation && (
            <Marker 
              position={userLocation}
              icon={L.divIcon({
                className: 'user-location-marker',
                html: `
                  <div style="
                    width: 16px;
                    height: 16px;
                    background: #3B82F6;
                    border-radius: 50%;
                    border: 3px solid white;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                  "></div>
                `,
                iconSize: [16, 16],
                iconAnchor: [8, 8],
              })}
            />
          )}
        </MapContainer>
        
        {/* Bouton géolocalisation flottant - Mobile uniquement */}
        <button
          onClick={useCurrentLocation}
          className="md:hidden absolute top-3 right-3 z-[600] w-10 h-10 bg-[#1E3A5F] hover:bg-[#152a45] rounded-xl shadow-lg flex items-center justify-center active:scale-95 transition-all"
          title="Ma position"
          data-testid="btn-geoloc-floating"
        >
          {geolocLoading
            ? <Loader2 size={18} className="text-white animate-spin" />
            : <Navigation size={18} className="text-white" />
          }
        </button>
      </div>
      
      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-[999]">
          <div className="flex flex-col items-center gap-3">
            <Loader2 size={40} className="text-[#F97316] animate-spin" />
            <p className="text-gray-600 font-medium">Recherche des artisans...</p>
          </div>
        </div>
      )}
      
      {/* Aucun artisan — état vide neutre */}
      {!loading && artisans.length === 0 && (
        <div className="absolute top-[196px] left-4 right-4 z-[600]">
          <div className="bg-white/90 backdrop-blur-sm border border-gray-200 px-4 py-3 rounded-xl text-sm text-gray-500 text-center shadow-sm">
            Aucun artisan dans cette zone pour le moment.
          </div>
        </div>
      )}
      
      {/* Artisans list at bottom */}
      {artisans.length > 0 && (
        <div className="absolute bottom-0 left-0 right-0 z-[500]">
          <div className="bg-white rounded-t-2xl shadow-2xl max-h-[30vh] overflow-hidden">
            <div className="px-4 py-2.5 border-b border-gray-100">
              <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-1.5" />
              <h3 className="font-semibold text-gray-900 text-sm">{artisans.length} artisan{artisans.length !== 1 ? 's' : ''} à proximité</h3>
            </div>
            <div className="overflow-y-auto max-h-[calc(30vh-45px)]">
              {artisans.map((artisan) => {
                const metierData = METIERS_DATA.find(m => m.id === artisan.metier?.toLowerCase()) || METIERS_DATA[0];
                const MetierIcon = metierData?.icon || Building2;
                
                return (
                  <button
                    key={artisan.id}
                    onClick={() => {
                      setSelectedArtisan(artisan);
                      if (artisan.latitude && artisan.longitude) {
                        setMapCenter([artisan.latitude, artisan.longitude]);
                        setMapZoom(14);
                      }
                    }}
                    className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors border-b border-gray-50"
                  >
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-gray-100">
                      {artisan.photo ? (
                        <img 
                          src={artisan.photo} 
                          alt={artisan.shop_name}
                          className="w-11 h-11 rounded-xl object-cover"
                        />
                      ) : (
                        <MetierIcon size={22} className="text-[#1E3A5F]" />
                      )}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="flex items-center gap-1.5">
                        <h4 className="font-medium text-gray-900 text-sm truncate">{artisan.shop_name}</h4>
                        {artisan.is_verified && (
                          <CheckCircle2 size={14} className="text-blue-500 flex-shrink-0" fill="#3B82F6" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">{artisan.metier} • {artisan.city}</p>
                    </div>
                    <div className="flex items-center gap-1 bg-amber-50 px-2 py-1 rounded-lg flex-shrink-0">
                      <Star size={12} className="text-amber-500 fill-amber-500" />
                      <span className="text-sm font-bold text-amber-700">{artisan.rating?.toFixed(1) || '0.0'}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
      
      {/* Detail panel */}
      <AnimatePresence>
        {selectedArtisan && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/30 z-[999]"
              onClick={() => setSelectedArtisan(null)}
            />
            <ArtisanDetailPanel 
              artisan={selectedArtisan} 
              onClose={() => setSelectedArtisan(null)}
              onViewProfile={handleViewProfile}
            />
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
